import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, firebaseConfigValid } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { ROLES } from '@/lib/roles';
import { WORKFLOW_STATES } from '@/lib/workflow';
import { canSeePractice } from '@/lib/territorio';

// Mantenere sincronizzato con DataContext.js → DITTA_VISIBLE_STATES.
// Le rules Firestore bloccano la lettura /practices per ruolo='ditta' se la query
// non include esplicitamente i filtri (ditta, workflowStatus in [...]).
const DITTA_VISIBLE_STATES = [
  WORKFLOW_STATES.APPROVAZIONE_HQ,
  WORKFLOW_STATES.CHIUSO,
  WORKFLOW_STATES.RIFIUTATO_DITTA,
];

// Stato condiviso tra tutte le istanze del hook + subscribers per reattività
let _previousLastSeen = null;
const _subscribers = new Set();
function _setPreviousLastSeen(value) {
  _previousLastSeen = value;
  _subscribers.forEach(fn => fn(value));
}

/**
 * Hook che monitora le pratiche in real-time e segnala quelle "nuove"
 * (aggiornate dopo l'ultimo accesso dell'utente).
 *
 * Ritorna:
 * - newCount: numero di pratiche con aggiornamenti non visti
 * - markAllSeen: funzione per resettare il contatore
 */
export function useNewBadge() {
  const { user, role, userProfile } = useAuth();
  const [newCount, setNewCount] = useState(0);
  const [newPracticeIds, setNewPracticeIds] = useState(new Set());
  const [lastSeen, setLastSeen] = useState(null);
  const [prevLastSeenState, setPrevLastSeenState] = useState(_previousLastSeen);

  // Sottoscrivi questo hook alle modifiche di _previousLastSeen
  useEffect(() => {
    _subscribers.add(setPrevLastSeenState);
    return () => _subscribers.delete(setPrevLastSeenState);
  }, []);

  // Listener real-time su user_state per lastSeenAt sempre aggiornato
  useEffect(() => {
    if (!firebaseConfigValid || !db || !user) return;
    const unsub = onSnapshot(doc(db, 'user_state', user.uid), (snap) => {
      if (snap.exists()) {
        // serverTimestamps: 'estimate' restituisce una stima locale del timestamp
        // durante il "pending" (tra setDoc e conferma server). Senza questa opzione
        // `data.lastSeenAt` sarebbe null in quella finestra e il fallback `new Date(0)`
        // farebbe sembrare nuove TUTTE le pratiche per il tempo di andata/ritorno
        // server. Bug particolarmente visibile in emulator (latency maggiore).
        const data = snap.data({ serverTimestamps: 'estimate' });
        const ts = data.lastSeenAt?.toDate?.();
        if (ts) {
          setLastSeen(ts);
        }
        // Difesa in profondita': se 'estimate' fallisse e ts fosse comunque null
        // (edge case improbabile), NON resettiamo lastSeen a epoch — manteniamo
        // il valore corrente, evitando il rimbalzo del badge a "totale".
      } else {
        setLastSeen(new Date(0));
      }
    }, (err) => {
      console.error('Error listening to user_state:', err);
      setLastSeen(new Date(0));
    });
    return unsub;
  }, [user]);

  // Listener real-time sulle pratiche
  useEffect(() => {
    if (!firebaseConfigValid || !db || !user || !lastSeen) return;
    // Ditta senza dittaAppartenenza: attendi profilo completo (evita query bloccata dalle rules).
    if (role === ROLES.DITTA && !userProfile?.dittaAppartenenza) return;
    // Fornitore / Ditta: filtra come richiesto dalle rules (altrimenti permission-denied).
    let practicesRef = collection(db, 'practices');
    let q;
    if (role === ROLES.FORNITORE && userProfile?.studioProgettazione) {
      q = query(practicesRef, where('studioProgettazione', '==', userProfile.studioProgettazione));
    } else if (role === ROLES.DITTA && userProfile?.dittaAppartenenza) {
      q = query(practicesRef,
        where('ditta', '==', userProfile.dittaAppartenenza),
        where('workflowStatus', 'in', DITTA_VISIBLE_STATES));
    } else {
      q = practicesRef;
    }
    const unsub = onSnapshot(q, (snapshot) => {
      let count = 0;
      const ids = new Set();
      snapshot.docs.forEach((d) => {
        const data = d.data();
        // Visibilita' territoriale Fastweb: il badge non deve contare pratiche
        // fuori dall'area dell'utente (coerente col filtro in DataContext).
        if (role === ROLES.FASTWEB && !canSeePractice(userProfile, data)) return;
        const updatedAt = data.updatedAt?.toDate?.();
        if (updatedAt && updatedAt > lastSeen && data.lastUpdatedBy !== user.uid) {
          count++;
          ids.add(data.id || d.id);
        }
      });
      setNewCount(count);
      setNewPracticeIds(ids);
    }, (err) => {
      console.error('Real-time listener error:', err);
    });
    return unsub;
  }, [user, lastSeen, role, userProfile]);

  const markAllSeen = useCallback(async () => {
    if (!firebaseConfigValid || !db || !user) return;
    // Salva il valore corrente prima di aggiornare (condiviso tra tutte le istanze)
    _setPreviousLastSeen(lastSeen);
    try {
      await setDoc(doc(db, 'user_state', user.uid), {
        lastSeenAt: serverTimestamp(),
      }, { merge: true });
      // lastSeen verrà aggiornato automaticamente dall'onSnapshot su user_state
    } catch (e) {
      console.error('Error marking as seen:', e);
    }
  }, [user, lastSeen]);

  const clearPreviousLastSeen = useCallback(() => { _setPreviousLastSeen(null); }, []);

  return { newCount, newPracticeIds, markAllSeen, previousLastSeen: prevLastSeenState, clearPreviousLastSeen };
}
